import React, { useEffect, useState } from 'react';
import { Empty, List, Typography } from 'antd';
import { F } from '@/utils/intl';
import classNames from 'classnames';
import { Message } from '@/components/Communication/types';
import { LeadModel } from '@/typings/models/Lead';
import { getMessageRenderer } from '@/components/Communication/factory';
import { fetchLeadSentMails, mapSentMailToMessage } from '@/services/api/communication';
import { processAPIResponseNow } from '@/utils/request';
import styles from './MessagesDrawer.less';

const { Title } = Typography;

export interface MessagesDrawerProps {
  projectId: string;
  project?: LeadModel;
  payload?: {
    selectedMessage?: Message;
  };
}

const MessagesDrawer: React.FC<MessagesDrawerProps> = ({
  projectId,
  project,
  payload,
}) => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [pageSize] = useState(10);
  const [selectedMessage, setSelectedMessage] = useState<Message | null>(
    payload?.selectedMessage || null,
  );

  const fetchMessages = (page: number, size: number) => {
    setLoading(true);
    fetchLeadSentMails(projectId, { page, page_size: size }).then((APIResponse: any) => {
      processAPIResponseNow(APIResponse, {
        onSuccess(response: any) {
          const mappedMessages = (response.data || []).map((m: any) =>
            mapSentMailToMessage(m, project?.customer?.email)
          );
          setMessages(mappedMessages);
          setTotal(response.meta?.total || mappedMessages.length);
          setLoading(false);

          // If we have a selected message from payload, make sure it's in the list or keep it
          if (payload?.selectedMessage && !selectedMessage) {
            setSelectedMessage(payload.selectedMessage);
          }
        },
        onError() {
          setLoading(false);
        }
      });
    });
  };

  useEffect(() => {
    if (projectId) {
      fetchMessages(currentPage, pageSize);
    }
  }, [projectId, currentPage, pageSize]);

  return (
    <div className={styles.drawerContainer}>
      <div className={styles.listColumn}>
        <List
          loading={loading}
          itemLayout="horizontal"
          dataSource={messages}
          pagination={{
            current: currentPage,
            total: total,
            pageSize: pageSize,
            size: 'small',
            simple: true,
            hideOnSinglePage: true,
            onChange: (page) => setCurrentPage(page),
          }}
          renderItem={(message) => {
            const Renderer = getMessageRenderer(message.medium).Extensive;
            return (
              <List.Item
                onClick={() => setSelectedMessage(message)}
                className={classNames(styles.listItem, {
                  [styles.selectedListItem]: selectedMessage?.id === message.id,
                })}
              >
                <Renderer message={message} />
              </List.Item>
            );
          }}
        />
      </div>

      <div className={styles.contentColumn}>
        {selectedMessage ? (
          <div className={styles.previewWrapper}>
            {(() => {
              const PreviewRenderer = getMessageRenderer(selectedMessage.medium).Preview;
              return <PreviewRenderer message={selectedMessage} />;
            })()}
          </div>
        ) : (
          <div className={styles.emptyContainer}>
            <Empty
              description={
                <F
                  id="communication.preview.empty"
                  defaultMessage="Select a message to view details"
                />
              }
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default MessagesDrawer;
